import React from 'react'
import FilterSelect from '@chegg-wt/core/components/FilterSelect'
import AppLink from '@chegg-wt/core/components/AppLink'
import { sourceTypeById } from '@chegg-wt/core/utils/sourceTypes'
import { AppRoute, SourceType } from '@chegg-wt/core/types/core'
import * as Styled from './styled'
import * as TestIds from './testIds'

interface Props {
  /** source types that should have buttons on the button bar */
  buttonSourceTypes: SourceType[]
  /** source types that should be listed in the dropdown */
  dropdownSourceTypes: SourceType[]
  /** action to perform when source type is selected */
  onSourceTypeSelect: (sourceType: SourceType) => void
  /** compose route */
  composeRoute: (sourceType: SourceType) => AppRoute
}

// use closure to capture placeholder for simplicity
const Trigger = ({ isOpen, openDropdown, selected }) => (
  <Styled.Trigger
    onClick={openDropdown}
    isHoverAnimated={!isOpen}
    data-test={TestIds.MORE_STYLES_BUTTON}
    aria-expanded={isOpen}
    aria-label={`${selected.name || 'More'} option`}
  >
    <Styled.ButtonText>{selected.name || 'More'}</Styled.ButtonText>
    <Styled.Chevron size="small" pointing={isOpen ? 'up' : 'down'} />
  </Styled.Trigger>
)

/** button bar with link buttons and a dropdown that select a source type */
const SourceTypeLinkPicker: React.FC<Props> = ({
  buttonSourceTypes,
  dropdownSourceTypes,
  onSourceTypeSelect,
  composeRoute,
}) => {
  const sourceTypeOptions = dropdownSourceTypes.map((sourceType) => ({
    ...sourceType,
    linkedRoute: composeRoute(sourceType),
  }))

  return (
    <>
      <Styled.GroupTitle>Choose your source:</Styled.GroupTitle>
      <Styled.ButtonBar
        role="group"
        aria-label="Select source type"
        data-test={TestIds.SOURCE_TYPE_PICKER}
      >
        {buttonSourceTypes.map((source) => (
          <AppLink route={composeRoute(source)} key={source.id}>
            <Styled.ButtonLink
              data-test={TestIds.SOURCE_TYPE_BUTTON(source.id)}
              aria-label={`Select ${source.name} option`}
            >
              {source.name}
            </Styled.ButtonLink>
          </AppLink>
        ))}
        <FilterSelect
          onSelect={(id) => onSourceTypeSelect(sourceTypeById(id))}
          options={sourceTypeOptions}
          trigger={Trigger}
          type="source-types"
          renderOptionAsLink
          isDropdownVisuallyHiddenOnInitialRender
        />
      </Styled.ButtonBar>
    </>
  )
}

export default SourceTypeLinkPicker
